import React, { useRef, useEffect, forwardRef, useImperativeHandle, useMemo, useCallback } from "react";
import {
  EntityType,
  MentionData,
  internalToDisplay,
  displayToInternal,
  parseDisplayMentions,
} from "@/hooks/use-entity-mentions";

// ============================================================================
// Ref Handle Type
// ============================================================================

export interface MentionInputHandle {
  /** The underlying textarea element */
  textarea: HTMLTextAreaElement | null;
  /** Get the current display value (for mention detection) */
  getDisplayValue: () => string;
  /** Get the current mention map (for entity selection) */
  getMentionMap: () => Map<string, MentionData>;
  /** Focus the textarea */
  focus: () => void;
  /** Set selection range on the textarea */
  setSelectionRange: (start: number, end: number) => void;
}

// ============================================================================
// Types
// ============================================================================

interface MentionInputProps {
  /** Current input value (with raw mention format @[type:id:name]) */
  value: string;
  /** Callback when value changes (receives internal format) */
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  /** Callback for mention detection (receives display value and cursor position) */
  onMentionDetect?: (displayValue: string, cursorPosition: number, element: HTMLTextAreaElement) => void;
  /** Callback for key events */
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Additional CSS classes for the container */
  className?: string;
  /** Theme object with color classes */
  theme: {
    textPrimary: string;
    textMuted: string;
  };
  /** Minimum number of rows */
  minRows?: number;
  /** Maximum number of rows before scrolling */
  maxRows?: number;
}

// ============================================================================
// Constants
// ============================================================================

const ENTITY_COLORS: Record<EntityType, string> = {
  tracker: "text-blue-400 bg-blue-500/10",
  dashboard: "text-purple-400 bg-purple-500/10",
  widget: "text-green-400 bg-green-500/10",
  enrichment: "text-orange-400 bg-orange-500/10",
  alert: "text-red-400 bg-red-500/10",
};

// ============================================================================
// Component
// ============================================================================

/**
 * Textarea component that displays entity mentions as styled chips.
 * Uses an overlay technique: invisible textarea for editing, visible layer for display.
 * Auto-resizes based on content.
 * 
 * Uses two values internally:
 * - displayValue: Shows clean @name format to user (cursor aligned)
 * - internalValue: Contains full @[type:id:name] format for backend
 */
export const MentionInput = forwardRef<MentionInputHandle, MentionInputProps>(
  (
    {
      value,
      onChange,
      onMentionDetect,
      onKeyDown,
      placeholder,
      disabled = false,
      className = "",
      theme,
      minRows = 1,
      maxRows = 6,
    },
    ref
  ) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const displayRef = useRef<HTMLDivElement>(null);
    
    // Store the mention map in a ref to persist across renders
    const mentionMapRef = useRef<Map<string, MentionData>>(new Map());

    // Convert internal value to display value
    const { displayValue, mentionMap } = useMemo(() => {
      return internalToDisplay(value);
    }, [value]);
    
    // Update mention map ref when it changes
    useEffect(() => {
      mentionMapRef.current = mentionMap;
    }, [mentionMap]);

    // Expose handle with textarea and helper methods
    useImperativeHandle(ref, () => ({
      textarea: textareaRef.current,
      getDisplayValue: () => displayValue,
      getMentionMap: () => mentionMapRef.current,
      focus: () => textareaRef.current?.focus(),
      setSelectionRange: (start: number, end: number) => {
        textareaRef.current?.setSelectionRange(start, end);
      },
    }), [displayValue]);

    // Auto-resize textarea based on content
    useEffect(() => {
      const textarea = textareaRef.current;
      if (!textarea) return;

      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = "auto";
      
      // Calculate line height (approximately 20px for text-sm)
      const lineHeight = 20;
      const minHeight = lineHeight * minRows;
      const maxHeight = lineHeight * maxRows;
      
      // Set height based on content, clamped between min and max
      const newHeight = Math.min(Math.max(textarea.scrollHeight, minHeight), maxHeight);
      textarea.style.height = `${newHeight}px`;
    }, [displayValue, minRows, maxRows]);

    // Sync scroll position between textarea and display
    useEffect(() => {
      const textarea = textareaRef.current;
      const display = displayRef.current;
      if (!textarea || !display) return;

      const handleScroll = () => {
        display.scrollTop = textarea.scrollTop;
        display.scrollLeft = textarea.scrollLeft;
      };

      textarea.addEventListener("scroll", handleScroll);
      return () => textarea.removeEventListener("scroll", handleScroll);
    }, []);

    // Find if cursor is inside or at the edge of a mention
    const findMentionAtPosition = useCallback(
      (position: number, mentions: ReturnType<typeof parseDisplayMentions>) => {
        for (const mention of mentions) {
          // Cursor is inside the mention (including right after it for backspace)
          if (position > mention.startIndex && position <= mention.endIndex) {
            return mention;
          }
        }
        return null;
      },
      []
    );

    // Handle keydown to intercept Backspace/Delete on mentions
    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        const textarea = textareaRef.current;
        if (!textarea) {
          onKeyDown?.(e);
          return;
        }

        const cursorStart = textarea.selectionStart;
        const cursorEnd = textarea.selectionEnd;
        const mentions = parseDisplayMentions(displayValue, mentionMap);

        // Handle Backspace
        if (e.key === "Backspace" && cursorStart === cursorEnd) {
          const mentionAtCursor = findMentionAtPosition(cursorStart, mentions);
          if (mentionAtCursor) {
            e.preventDefault();
            // Remove the entire mention
            const newDisplayValue = 
              displayValue.slice(0, mentionAtCursor.startIndex) + 
              displayValue.slice(mentionAtCursor.endIndex);
            
            // Remove from mention map
            const newMentionMap = new Map(mentionMapRef.current);
            newMentionMap.delete(mentionAtCursor.displayName);
            mentionMapRef.current = newMentionMap;
            
            // Convert to internal value and trigger onChange
            const newInternalValue = displayToInternal(newDisplayValue, newMentionMap);
            const syntheticEvent = {
              target: { value: newInternalValue },
              currentTarget: { value: newInternalValue },
            } as React.ChangeEvent<HTMLTextAreaElement>;
            onChange(syntheticEvent);
            
            // Set cursor position after deletion
            setTimeout(() => {
              textarea.setSelectionRange(mentionAtCursor.startIndex, mentionAtCursor.startIndex);
            }, 0);
            return;
          }
        }

        // Handle Delete
        if (e.key === "Delete" && cursorStart === cursorEnd) {
          // Check if cursor is right before a mention
          const mentionAfterCursor = mentions.find(m => m.startIndex === cursorStart);
          if (mentionAfterCursor) {
            e.preventDefault();
            // Remove the entire mention
            const newDisplayValue = 
              displayValue.slice(0, mentionAfterCursor.startIndex) + 
              displayValue.slice(mentionAfterCursor.endIndex);
            
            // Remove from mention map
            const newMentionMap = new Map(mentionMapRef.current);
            newMentionMap.delete(mentionAfterCursor.displayName);
            mentionMapRef.current = newMentionMap;
            
            // Convert to internal value and trigger onChange
            const newInternalValue = displayToInternal(newDisplayValue, newMentionMap);
            const syntheticEvent = {
              target: { value: newInternalValue },
              currentTarget: { value: newInternalValue },
            } as React.ChangeEvent<HTMLTextAreaElement>;
            onChange(syntheticEvent);
            return;
          }
        }

        // Pass through to parent handler
        onKeyDown?.(e);
      },
      [displayValue, mentionMap, onChange, onKeyDown, findMentionAtPosition]
    );

    // Handle change - convert display value back to internal value
    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newDisplayValue = e.target.value;
        const cursorPosition = e.target.selectionStart ?? newDisplayValue.length;
        
        // Convert display value back to internal value
        const newInternalValue = displayToInternal(newDisplayValue, mentionMapRef.current);
        
        // Call original onChange with internal value
        const syntheticEvent = {
          ...e,
          target: { ...e.target, value: newInternalValue },
          currentTarget: { ...e.currentTarget, value: newInternalValue },
        } as React.ChangeEvent<HTMLTextAreaElement>;
        onChange(syntheticEvent);
        
        // Call mention detection with display value and cursor position (after state update)
        if (onMentionDetect) {
          setTimeout(() => {
            onMentionDetect(newDisplayValue, cursorPosition, e.target);
          }, 0);
        }
      },
      [onChange, onMentionDetect]
    );

    // Render the display content with chips
    const renderDisplayContent = () => {
      if (!displayValue) {
        return (
          <span className={`${theme.textMuted} opacity-60`}>
            {placeholder}
          </span>
        );
      }

      const mentions = parseDisplayMentions(displayValue, mentionMap);

      // If no mentions, render plain text
      if (mentions.length === 0) {
        return <span>{displayValue}</span>;
      }

      // Build array of text segments and mention chips
      const parts: React.ReactNode[] = [];
      let lastIndex = 0;

      mentions.forEach((mention, index) => {
        // Add text before this mention
        if (mention.startIndex > lastIndex) {
          parts.push(
            <span key={`text-${index}`}>
              {displayValue.slice(lastIndex, mention.startIndex)}
            </span>
          );
        }

        // Style the mention text (no extra elements to keep cursor aligned)
        const colorClasses = ENTITY_COLORS[mention.type] || ENTITY_COLORS.tracker;

        parts.push(
          <span
            key={`mention-${index}`}
            className={`font-medium ${colorClasses}`}
            title={`${mention.type}: ${mention.name}`}
            style={{ whiteSpace: "pre-wrap" }}
          >
            {mention.displayName}
          </span>
        );

        lastIndex = mention.endIndex;
      });

      // Add remaining text after last mention
      if (lastIndex < displayValue.length) {
        parts.push(
          <span key="text-end">{displayValue.slice(lastIndex)}</span>
        );
      }

      return <>{parts}</>;
    };

    return (
      <div className={`relative flex-1 ${className}`}>
        {/* Visible display layer with chips */}
        <div
          ref={displayRef}
          className={`
            absolute inset-0 p-0
            text-sm pointer-events-none overflow-auto
            whitespace-pre-wrap break-words
            ${theme.textPrimary}
          `}
          aria-hidden="true"
        >
          {renderDisplayContent()}
        </div>

        {/* Invisible textarea for actual editing - uses displayValue for cursor alignment */}
        <textarea
          ref={textareaRef}
          value={displayValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder=""
          disabled={disabled}
          rows={minRows}
          className={`
            w-full bg-transparent text-sm outline-none resize-none
            text-transparent
            disabled:cursor-not-allowed
            focus:ring-0 focus:shadow-none focus:border-transparent
            ${theme.textPrimary}
          `}
          style={{
            caretColor: "var(--arc-sidebar-text-primary, currentColor)",
            color: "transparent",
          }}
        />
      </div>
    );
  }
);

MentionInput.displayName = "MentionInput";

export default MentionInput;
